import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type RecordingMode = 'single' | 'segmented';

export interface RecordingState {
  isRecording: boolean;
  recordingTime: Date;
  startTime: number;
}

export interface ProcessedRecording {
  base64: string;
  mimeType: string;
}

export interface RecordingSegment {
  blob: Blob;
  index: number;
  mimeType: string;
  isFinal: boolean;
}

/**
 * The recorder paused itself because the microphone went away (a phone call, a headset
 * unplugged, another app taking the device), or carried on after one. `resumed` is false when
 * capture could not come back on its own; Resume then tries again.
 */
export interface RecordingInterruption {
  state: 'began' | 'ended';
  resumed?: boolean;
  reason?: string;
}

export interface RecordingServiceConfig {
  mode?: RecordingMode;
  segmentLengthMs?: number;
  onSegment?: (seg: RecordingSegment) => void;
  onBlobReady?: (rec: ProcessedRecording) => void;
  onStateChange?: (state: RecordingState) => void;
  onError?: (err: string) => void;
  onInterruption?: (event: RecordingInterruption) => void;
}

@Injectable({ providedIn: 'root' })
export class RecordingService {
  private mediaRecorder: any = null;
  private recordingStream: MediaStream | null = null;
  private rtcRef: any = null;
  private useLegacyRecorder: boolean = false;
  private currentMimeType: string = '';

  private blobProcessed: boolean = false;
  private blobTimeout: any = null;
  private recordingInterval: any = null;
  private sliceInterval: any = null;
  private recordingStartTime: number = 0;
  private segmentIndex: number = 0;
  private paused: boolean = false;
  private stopResolver: (() => void) | null = null;

  /** Set once the capture has been declared dead, so the loss is reported exactly once. */
  /**
   * True from the moment a DELIBERATE stop begins until the next `startRecording()`.
   *
   * Stopping a MediaStreamTrack ends it, and a MediaStream whose tracks have all ended fires
   * `inactive` — the very event `watchCapture()` treats as lost capture. So every normal stop
   * tripped the capture watcher, because the three paths that release the stream do it while
   * `mediaRecorder` is still set and `isRecording()` therefore still reports true. Reported as:
   * every press of Stop in the AI dialog raised "Recording stopped unexpectedly — the audio
   * stream ended".
   *
   * `recording.service.native.ts` has carried exactly this flag, under exactly this name, since
   * the native recorder shipped: its plugin emits a final `stateChange` on stop for the same
   * reason. The web implementation was the one that never got it.
   */
  private stopping: boolean = false;
  private captureLost: boolean = false;
  /** The microphone went away mid-recording; paused until Resume opens a fresh one. */
  private interrupted: boolean = false;
  /** Set while an interrupted recorder hands over its last audio, so it is not taken as the end. */
  private flushing: boolean = false;
  /** Pending confirmation that a `mute` is a real stall rather than a momentary one. */
  private muteTimeout: any = null;
  /**
   * When a segment last arrived, for the segment watchdog. Zero while no segmented recording
   * is running, which is also how the watchdog knows to stay quiet.
   */
  private lastSegmentAt: number = 0;
  /** Bound `visibilitychange` handler, kept so `cleanup()` can detach it. */
  private visibilityHandler: (() => void) | null = null;

  /**
   * How long a live-but-muted track is tolerated before it counts as lost capture.
   *
   * A `mute` on its own means nothing: WebKit fires one whenever the page stops being the
   * foreground audio client, including every time the user glances at another app. Sustained
   * mute WHILE THE PAGE IS VISIBLE is different — that is the microphone having been taken
   * away with the track left open, which is the failure that produces a full-length recording
   * of silence.
   */
  private static readonly MUTE_GRACE_MS = 3000;

  /**
   * How many slice periods may pass with no segment before capture counts as lost.
   *
   * `watchCapture()` above asks "is the microphone still alive?". This asks the question
   * nobody was asking: "are segments still arriving?" They are not the same question, and the
   * gap between them is a total, silent loss of a consultation.
   *
   * `StereoAudioRecorder`'s interval loop (`src/libs/recordrtc.js`, `looper`) re-arms itself
   * ONLY from inside the callback of the Web Worker that builds each slice's WAV — a worker
   * created per slice from a blob: URL with no `onerror` handler. If that worker never
   * answers, or `ondataavailable` throws, or the recorder's internal `recording` flag flips,
   * the loop stops for good with no event, no error and no state change. The MediaStreamTrack
   * stays live and unmuted throughout, so every signal `watchCapture()` listens for says the
   * recording is healthy. The non-WebKit path fails the same way for a different reason:
   * `processManualSegment()` restarts the recorder inside a callback that may never fire.
   *
   * 1.5 absorbs the ~120 ms merge each slice costs and a backgrounded tab's timer clamping,
   * without absorbing a loop that has stopped. See `tasks/recording-segmentation-stall.md`.
   */
  private static readonly SEGMENT_WATCHDOG_SLICES = 1.5;

  private static readonly DEFAULT_MODE: RecordingMode = 'single';
  private static readonly DEFAULT_SEGMENT_LENGTH_MS = 60000;

  private mode: RecordingMode = RecordingService.DEFAULT_MODE;
  private segmentLengthMs: number = RecordingService.DEFAULT_SEGMENT_LENGTH_MS;

  private onBlobReadyCallback?: (rec: ProcessedRecording) => void;
  private onSegmentCallback?: (seg: RecordingSegment) => void;
  private onErrorCallback?: (err: string) => void;
  private onStateChangeCallback?: (state: RecordingState) => void;
  private onInterruptionCallback?: (event: RecordingInterruption) => void;

  constructor(@Inject(PLATFORM_ID) private platformId: object) { }

  configure(config: RecordingServiceConfig): void {
    if (config.mode) this.mode = config.mode;
    if (config.segmentLengthMs !== undefined) this.segmentLengthMs = config.segmentLengthMs;
    if (config.onSegment !== undefined) this.onSegmentCallback = config.onSegment;
    if (config.onBlobReady !== undefined) this.onBlobReadyCallback = config.onBlobReady;
    if (config.onStateChange !== undefined) this.onStateChangeCallback = config.onStateChange;
    if (config.onError !== undefined) this.onErrorCallback = config.onError;
    if (config.onInterruption !== undefined) this.onInterruptionCallback = config.onInterruption;
  }

  setBlobReadyCallback(cb: (rec: ProcessedRecording) => void): void { this.onBlobReadyCallback = cb; }
  setErrorCallback(cb: (err: string) => void): void { this.onErrorCallback = cb; }
  setStateChangeCallback(cb: (state: RecordingState) => void): void { this.onStateChangeCallback = cb; }
  setSegmentCallback(cb: (seg: RecordingSegment) => void): void { this.onSegmentCallback = cb; }

  async initializeMediaRecorder(): Promise<boolean> {
    try {
      if (!isPlatformBrowser(this.platformId)) {
        return false;
      }

      const RecordRTC = await import('src/libs/recordrtc') as any;
      const RTC = RecordRTC.default || RecordRTC;
      this.rtcRef = RTC;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.recordingStream = stream;
      this.watchCapture(stream);
      this.watchVisibility();

      const ua = navigator.userAgent || '';
      const isWebKit = /AppleWebKit/i.test(ua) && !/Edge|OPR/i.test(ua);
      const hasNative = typeof window.MediaRecorder !== 'undefined';

      let recorderType: any = RTC.MediaStreamRecorder;
      let mimeType: string = 'audio/webm';
      this.useLegacyRecorder = false;

      if (isWebKit) {
        console.log('[RECORDING] WebKit path (StereoAudioRecorder + WAV)');
        recorderType = RTC.StereoAudioRecorder;
        mimeType = 'audio/wav';
        this.useLegacyRecorder = true;
      } else {
        console.log('[RECORDING] Non-WebKit path (MediaStreamRecorder)');
        const opusCandidates = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/ogg'
        ];
        const supported = opusCandidates.find(m =>
          hasNative &&
          typeof MediaRecorder.isTypeSupported === 'function' &&
          MediaRecorder.isTypeSupported(m)
        );
        if (supported) mimeType = supported;
        else mimeType = 'audio/ogg';
      }

      this.currentMimeType = mimeType;

      const baseConfig: any = {
        type: 'audio',
        recorderType,
        mimeType,
        numberOfAudioChannels: 1,
        disableLogs: false
      };

      if (isWebKit) {
        baseConfig.timeSlice = this.mode === 'segmented' ? this.segmentLengthMs : 600000;
        baseConfig.ondataavailable = (blob: Blob, final?: boolean) => this.handleWebKitDataAvailable(blob, final);
      }

      this.mediaRecorder = new RTC(stream, baseConfig);

      console.log('[RECORDING] Initialized', {
        mode: this.mode,
        segmentLengthMs: this.segmentLengthMs,
        recorderType: recorderType === RTC.StereoAudioRecorder ? 'StereoAudioRecorder' : 'MediaStreamRecorder',
        mimeType,
        useLegacyRecorder: this.useLegacyRecorder
      });

      return true;
    } catch (error) {
      console.error('[RECORDING] Failed to initialize:', error);
      return false;
    }
  }

  async startRecording(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) return false;
    if (this.isRecording()) return false;

    try {
      if (this.mediaRecorder) {
        try { this.mediaRecorder.destroy(); } catch { /* ignore */ }
        this.mediaRecorder = null;
      }
      this.releaseStream();

      const initialized = await this.initializeMediaRecorder();
      if (!initialized) {
        if (this.onErrorCallback) {
          this.onErrorCallback('Failed to initialize recording. Please try again.');
        }
        return false;
      }

      this.blobProcessed = false;
      this.segmentIndex = 0;
      this.paused = false;
      // CLEARED HERE AND NOWHERE ELSE, `cleanup()` included. `cleanup()` releases the stream
      // too, and clearing the flag there would leave the `inactive` event it queues arriving
      // into a service that has forgotten it asked for the stop.
      this.stopping = false;
      this.captureLost = false;
      this.interrupted = false;
      this.flushing = false;
      this.recordingStartTime = Date.now();
      // The first slice is due one full period from here, so the watchdog's clock starts now
      // rather than at the first segment — otherwise a loop that never produced a single
      // segment would never be caught.
      this.lastSegmentAt = this.recordingStartTime;

      if (this.blobTimeout) { clearTimeout(this.blobTimeout); this.blobTimeout = null; }

      let recordingTime = new Date(0);
      this.recordingInterval = setInterval(() => {
        if (this.paused) return;
        recordingTime = new Date(recordingTime.getTime() + 1000);
        if (this.onStateChangeCallback) {
          this.onStateChangeCallback({
            isRecording: true,
            recordingTime,
            startTime: this.recordingStartTime
          });
        }
        // Rides the existing heartbeat rather than adding a timer of its own. Runs after the
        // state callback so the consumer's clock still shows the second that just elapsed
        // before the stall freezes it.
        this.checkSegmentWatchdog();
      }, 1000);

      this.mediaRecorder.startRecording();

      if (this.mode === 'segmented' && !this.useLegacyRecorder) {
        this.sliceInterval = setInterval(() => this.processManualSegment(), this.segmentLengthMs);
      }

      console.log('[RECORDING] Started', {
        mode: this.mode,
        recorderType: this.useLegacyRecorder ? 'StereoAudioRecorder' : 'MediaStreamRecorder',
        startTime: this.recordingStartTime
      });

      return true;
    } catch (error) {
      console.error('[RECORDING] Error starting:', error);
      if (this.onErrorCallback) {
        this.onErrorCallback('Failed to start recording. Please check microphone permissions.');
      }
      this.cleanup();
      return false;
    }
  }

  async pauseRecording(): Promise<void> {
    if (!this.isRecording() || this.paused) return;
    try {
      if (this.mediaRecorder && typeof this.mediaRecorder.pauseRecording === 'function') {
        this.mediaRecorder.pauseRecording();
      }
      if (this.sliceInterval) { clearInterval(this.sliceInterval); this.sliceInterval = null; }
      this.paused = true;
    } catch (error) {
      console.error('[RECORDING] Error pausing:', error);
    }
  }

  async resumeRecording(): Promise<void> {
    if (!this.isRecording() || !this.paused) return;
    if (this.interrupted) {
      await this.resumeAfterInterruption();
      return;
    }
    try {
      if (this.mediaRecorder && typeof this.mediaRecorder.resumeRecording === 'function') {
        this.mediaRecorder.resumeRecording();
      }
      if (this.mode === 'segmented' && !this.useLegacyRecorder) {
        this.sliceInterval = setInterval(() => this.processManualSegment(), this.segmentLengthMs);
      }
      // A pause of any length leaves `lastSegmentAt` stale. The watchdog is suppressed while
      // paused, so without this the first tick after a resume would trip it immediately.
      this.lastSegmentAt = Date.now();
      this.paused = false;
    } catch (error) {
      console.error('[RECORDING] Error resuming:', error);
    }
  }

  async stopRecording(): Promise<void> {
    if (!this.isRecording() || !this.mediaRecorder) {
      console.log('[RECORDING] stopRecording called but not recording');
      return;
    }

    // BEFORE anything is torn down, for the reason `stopping` records: the first thing the
    // teardown does is give the microphone back, and that alone looks like lost capture.
    this.stopping = true;

    // The interruption already handed over everything this recorder captured and gave the
    // microphone back; there is nothing left to stop.
    if (this.interrupted) {
      if (this.recordingInterval) { clearInterval(this.recordingInterval); this.recordingInterval = null; }
      if (this.onStateChangeCallback) {
        this.onStateChangeCallback({ isRecording: false, recordingTime: new Date(0), startTime: this.recordingStartTime });
      }
      return;
    }

    const recordingDuration = Date.now() - this.recordingStartTime;
    console.log('[RECORDING] Stopping', {
      mode: this.mode,
      duration: recordingDuration,
      useLegacyRecorder: this.useLegacyRecorder
    });

    try {
      if (this.recordingInterval) { clearInterval(this.recordingInterval); this.recordingInterval = null; }
      if (this.sliceInterval) { clearInterval(this.sliceInterval); this.sliceInterval = null; }
      if (this.blobTimeout) { clearTimeout(this.blobTimeout); this.blobTimeout = null; }

      const stopPromise = new Promise<void>((resolve) => { this.stopResolver = resolve; });

      if (this.useLegacyRecorder) {
        this.mediaRecorder.stopRecording();
        this.blobTimeout = setTimeout(() => {
          if (!this.blobProcessed) {
            console.warn('[RECORDING] Timeout waiting for final blob - cleaning up');
            this.cleanup();
            if (this.stopResolver) { this.stopResolver(); this.stopResolver = null; }
          }
          this.blobTimeout = null;
        }, 50000);
      } else {
        this.mediaRecorder.stopRecording(() => {
          try {
            const blob = this.mediaRecorder.getBlob();
            this.handleNonWebKitStopBlob(blob);
          } catch (err) {
            console.error('[RECORDING] Error getting final blob:', err);
            if (this.onErrorCallback) this.onErrorCallback('Error finalizing recording.');
            this.cleanup();
          }
          if (this.stopResolver) { this.stopResolver(); this.stopResolver = null; }
        });
      }

      if (this.onStateChangeCallback) {
        this.onStateChangeCallback({
          isRecording: false,
          recordingTime: new Date(0),
          startTime: this.recordingStartTime
        });
      }

      await stopPromise;
    } catch (error) {
      console.error('[RECORDING] Error stopping:', error);
      if (this.onErrorCallback) this.onErrorCallback('Error stopping recording. Please try again.');
      this.cleanup();
    }
  }

  /**
   * Watches the captured MediaStream and reports the moment it stops delivering audio.
   *
   * Nothing downstream can tell on its own. `StereoAudioRecorder` — the WebKit path, so every
   * iPhone and iPad — reacts to a dead stream by setting its internal `recording` flag to
   * false and disconnecting its audio node, with no callback, no event and no error
   * (`src/libs/recordrtc.js`, `onAudioProcessDataAvailable`). `isRecording()` here keeps
   * returning true because the recorder object and the start time are both still set, and
   * consumers keep their timers running. The result is a clock counting up over dead air and
   * a consultation that produces nothing — reported as "it stopped recording without me
   * noticing".
   *
   * `ended` and `inactive` are terminal per spec. `mute` is not, so it is only believed after
   * MUTE_GRACE_MS and only while the page is visible; see MUTE_GRACE_MS.
   */
  private watchCapture(stream: MediaStream): void {
    const tracks = stream.getAudioTracks();

    tracks.forEach((track: MediaStreamTrack) => {
      track.addEventListener('ended',
        () => this.handleCaptureLost('the microphone was disconnected', stream));

      track.addEventListener('mute', () => {
        if (this.muteTimeout) return;
        this.muteTimeout = setTimeout(() => {
          this.muteTimeout = null;
          // Backgrounded: the mute is the OS doing its job, and capture resumes on return.
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
          this.handleCaptureLost('the microphone stopped delivering audio', stream);
        }, RecordingService.MUTE_GRACE_MS);
      });

      track.addEventListener('unmute', () => {
        if (this.muteTimeout) { clearTimeout(this.muteTimeout); this.muteTimeout = null; }
      });
    });

    stream.addEventListener('inactive', () => this.handleCaptureLost('the audio stream ended', stream));
  }

  /**
   * Records that a segment reached a consumer, which is the only thing that satisfies the
   * watchdog. Called from every path that emits one, on both recorder implementations.
   */
  private noteSegmentEmitted(): void {
    this.lastSegmentAt = Date.now();
  }

  /**
   * Restarts the watchdog's window when the page comes back to the foreground.
   *
   * `checkSegmentWatchdog()` pushes the deadline forward on every tick it sees while hidden,
   * which covers a merely throttled tab. It does not cover a *frozen* one: Chrome's tab
   * freezing runs no timers at all, so nothing pushes the deadline, and the first tick after
   * a resume would see the whole frozen period as one overdue segment. This event fires on
   * the way back in either case.
   */
  private watchVisibility(): void {
    if (typeof document === 'undefined' || this.visibilityHandler) return;

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.lastSegmentAt = Date.now();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Declares capture lost when segments stop arriving, whatever the microphone claims.
   *
   * Only meaningful in 'segmented' mode: 'single' mode sets a 10-minute `timeSlice` on the
   * WebKit path purely to keep RecordRTC's internal buffering alive and discards every
   * intermediate chunk, so there is no segment cadence to police. WhatsApp voice notes share
   * this service and must not be affected.
   */
  private checkSegmentWatchdog(): void {
    if (this.mode !== 'segmented') return;
    if (this.paused || this.captureLost) return;
    if (!this.lastSegmentAt) return;

    // Same policy as the `mute` handling above, for the same reason: a hidden page is not a
    // failing one. Browsers clamp timers and may stop a main-thread ScriptProcessorNode
    // outright while a tab is backgrounded, so a clinician glancing at another window would
    // otherwise come back to a red "recording stopped" banner and a frozen clock on a
    // recording that is fine. Pushing the deadline forward each tick means returning to the
    // tab starts a fresh window rather than tripping on the gap that just passed.
    //
    // Audio genuinely lost while hidden is a real and separate defect — it splices rather
    // than stops — and is tracked in `tasks/recording-audio-gaps.md`, not here.
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      this.lastSegmentAt = Date.now();
      return;
    }

    const overdueBy = Date.now() - this.lastSegmentAt;
    const limit = this.segmentLengthMs * RecordingService.SEGMENT_WATCHDOG_SLICES;
    if (overdueBy <= limit) return;

    console.warn('[RECORDING] Segment watchdog tripped', {
      overdueBy,
      limit,
      segmentLengthMs: this.segmentLengthMs,
      segmentsEmitted: this.segmentIndex,
      elapsedMs: Date.now() - this.recordingStartTime,
      useLegacyRecorder: this.useLegacyRecorder
    });

    this.handleCaptureLost('the recorder stopped producing audio segments');
  }

  /**
   * Reports lost capture once, and stops the state timer so no consumer keeps counting.
   *
   * Deliberately does NOT tear the recorder down: whatever was captured before the stream
   * died is still held by the recorder, and the consumer must be able to stop normally and
   * keep it. This only tells the truth about what is happening now.
   */
  /**
   * Gives the microphone back, and records that WE asked for it.
   *
   * Four sites used to inline these three lines. That was not merely duplication: stopping the
   * tracks is indistinguishable, from `watchCapture()`'s side, from the microphone being pulled
   * out — the flag is the only thing that tells the two apart, and a fourth copy of the release
   * that forgot to set it would put the false alarm straight back. One function, one flag.
   */
  private releaseStream(): void {
    this.stopping = true;
    if (!this.recordingStream) return;
    this.recordingStream.getTracks().forEach((t: MediaStreamTrack) => t.stop());
    this.recordingStream = null;
  }

  private handleCaptureLost(reason: string, stream?: MediaStream): void {
    if (this.captureLost) return;
    if (!this.isRecording()) return;
    // A LAST SESSION'S STREAM, still holding listeners. `startRecording()` releases a leftover
    // stream before it opens a new one, and the `inactive` that release queues outlives the
    // flag below. The watchdog passes no stream and is exempt: it is about the recorder, not
    // about one capture. (`checkSegmentWatchdog`.)
    if (stream && stream !== this.recordingStream) return;
    // WE stopped it. `isRecording()` above is not enough on its own: the stream is released
    // while `mediaRecorder` is still set, and the `inactive` event it queues arrives after
    // that block has returned. See `stopping`.
    if (this.stopping) return;

    if (this.interrupted) return;

    // A microphone that went away is an interruption in a consultation recording: keep what was
    // captured and pause. The watchdog (no stream) is different: the microphone is fine and the
    // recorder is not, so that stays lost capture. Single mode (voice notes) is never split.
    if (stream && this.mode === 'segmented') {
      this.beginInterruption(reason);
      return;
    }

    this.captureLost = true;
    console.warn('[RECORDING] Capture lost:', reason);

    if (this.muteTimeout) { clearTimeout(this.muteTimeout); this.muteTimeout = null; }
    if (this.recordingInterval) { clearInterval(this.recordingInterval); this.recordingInterval = null; }

    // Error before state: both reach the AI dialog and both mark the recording stalled, but
    // only this one carries the reason, and the first message to arrive is the one kept.
    if (this.onErrorCallback) {
      this.onErrorCallback(`CAPTURE_LOST: ${reason}`);
    }
    if (this.onStateChangeCallback) {
      this.onStateChangeCallback({
        isRecording: false,
        recordingTime: this.getRecordingTime(),
        startTime: this.recordingStartTime
      });
    }
  }

  /**
   * Pauses on a microphone that went away, and hands over what the recorder holds as an ordinary
   * segment there and then, so it is uploaded and stored before anything else can go wrong.
   */
  private beginInterruption(reason: string): void {
    this.interrupted = true;
    this.paused = true;
    console.warn('[RECORDING] Interrupted:', reason);

    if (this.muteTimeout) { clearTimeout(this.muteTimeout); this.muteTimeout = null; }
    if (this.sliceInterval) { clearInterval(this.sliceInterval); this.sliceInterval = null; }

    const recorder = this.mediaRecorder;
    try {
      if (this.useLegacyRecorder) {
        this.flushing = true;
        recorder.stopRecording();
      } else {
        recorder.stopRecording(() => {
          try {
            this.emitSegment(recorder.getBlob(), false);
          } catch (err) {
            console.error('[RECORDING] Could not flush interrupted recorder:', err);
          }
        });
      }
    } catch (err) {
      this.flushing = false;
      console.error('[RECORDING] Could not stop interrupted recorder:', err);
    }
    this.releaseStream();

    if (this.onInterruptionCallback) this.onInterruptionCallback({ state: 'began', reason });
  }

  /** Resume after an interruption: a fresh microphone and recorder, segment numbering continuing. */
  private async resumeAfterInterruption(): Promise<void> {
    const spent = this.mediaRecorder;
    const initialized = await this.initializeMediaRecorder();
    if (!initialized) {
      if (this.onErrorCallback) {
        this.onErrorCallback('The microphone is still unavailable. End the call or reconnect the microphone, then press Resume.');
      }
      return;
    }
    if (spent && spent !== this.mediaRecorder) {
      try { spent.destroy(); } catch { /* ignore */ }
    }

    this.stopping = false;
    this.interrupted = false;
    this.paused = false;
    this.lastSegmentAt = Date.now();
    this.mediaRecorder.startRecording();
    if (this.mode === 'segmented' && !this.useLegacyRecorder) {
      this.sliceInterval = setInterval(() => this.processManualSegment(), this.segmentLengthMs);
    }
    if (this.onInterruptionCallback) this.onInterruptionCallback({ state: 'ended', resumed: true });
  }

  private emitSegment(blob: Blob, isFinal: boolean): void {
    if (!blob || blob.size === 0) return;
    const seg: RecordingSegment = {
      blob,
      index: this.segmentIndex++,
      mimeType: blob.type || this.currentMimeType,
      isFinal
    };
    this.noteSegmentEmitted();
    if (this.onSegmentCallback) this.onSegmentCallback(seg);
  }

  private handleWebKitDataAvailable(blob: Blob, final?: boolean): void {
    if (final === true && this.flushing) {
      this.flushing = false;
      this.emitSegment(blob, false);
      return;
    }
    const isFinal = final === true;
    const isIntermediate = final === undefined || final === false;

    if (this.mode === 'segmented') {
      if (!blob || blob.size === 0) return;
      const seg: RecordingSegment = {
        blob,
        index: this.segmentIndex++,
        mimeType: blob.type || this.currentMimeType,
        isFinal
      };
      console.log('[RECORDING] Segment (WebKit)', { index: seg.index, size: blob.size, isFinal });
      // Before the callback, not after: the segment has demonstrably arrived at this point,
      // and `onSegmentCallback` is a synchronous hop into the consumer that must not be able
      // to make a healthy slice look overdue.
      this.noteSegmentEmitted();
      if (this.onSegmentCallback) this.onSegmentCallback(seg);
      if (isFinal) {
        this.blobProcessed = true;
        if (this.blobTimeout) { clearTimeout(this.blobTimeout); this.blobTimeout = null; }
        this.releaseStream();
        if (this.stopResolver) { this.stopResolver(); this.stopResolver = null; }
      }
      return;
    }

    if (isIntermediate) {
      console.log('[RECORDING] Ignoring intermediate chunk (single mode)', { size: blob?.size });
      return;
    }

    if (isFinal && blob && blob.size > 0 && !this.blobProcessed) {
      this.blobProcessed = true;
      this.processSingleBlob(blob);
      if (this.stopResolver) { this.stopResolver(); this.stopResolver = null; }
    }
  }

  private handleNonWebKitStopBlob(blob: Blob): void {
    if (this.mode === 'segmented') {
      if (blob && blob.size > 0) {
        const seg: RecordingSegment = {
          blob,
          index: this.segmentIndex++,
          mimeType: blob.type || this.currentMimeType,
          isFinal: true
        };
        console.log('[RECORDING] Final segment (non-WebKit)', { index: seg.index, size: blob.size });
        this.noteSegmentEmitted();
        if (this.onSegmentCallback) this.onSegmentCallback(seg);
      }
      this.releaseStream();
      this.blobProcessed = true;
      return;
    }
    this.processSingleBlob(blob);
  }

  private processManualSegment(): void {
    if (!this.mediaRecorder || this.paused) return;
    try {
      this.mediaRecorder.stopRecording(() => {
        try {
          const blob = this.mediaRecorder.getBlob();
          if (blob && blob.size > 0) {
            const seg: RecordingSegment = {
              blob,
              index: this.segmentIndex++,
              mimeType: blob.type || this.currentMimeType,
              isFinal: false
            };
            console.log('[RECORDING] Interim segment (non-WebKit)', { index: seg.index, size: blob.size });
            this.noteSegmentEmitted();
            if (this.onSegmentCallback) this.onSegmentCallback(seg);
          }
          this.mediaRecorder.startRecording();
        } catch (err) {
          console.error('[RECORDING] Manual segment error:', err);
        }
      });
    } catch (err) {
      console.error('[RECORDING] Manual segment outer error:', err);
    }
  }

  private processSingleBlob(blob: Blob): void {
    if (!blob || blob.size === 0) {
      console.error('[RECORDING] Blob is empty or invalid', { size: blob?.size, type: blob?.type });
      if (this.onErrorCallback) this.onErrorCallback('Recording failed. Please try again.');
      this.cleanup();
      return;
    }

    console.log('[RECORDING] Got single blob', { size: blob.size, type: blob.type });

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader.result as string;
      const base64 = base64Data.split(',')[1];

      let mimeType = blob.type || 'audio/ogg';
      if (mimeType.includes('webm')) {
        if (mimeType.includes('opus') || mimeType.includes('codecs=opus')) {
          mimeType = 'audio/opus';
        } else {
          mimeType = 'audio/ogg';
        }
      }
      const supportedTypes = ['audio/ogg', 'audio/opus', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/aac'];
      if (!supportedTypes.includes(mimeType) && mimeType !== 'audio/wav') {
        mimeType = 'audio/ogg';
      }

      console.log('[RECORDING] Mapped MIME for single blob', { originalType: blob.type, mappedType: mimeType, blobSize: blob.size });

      if (this.blobTimeout) { clearTimeout(this.blobTimeout); this.blobTimeout = null; }
      this.releaseStream();

      if (this.onBlobReadyCallback) {
        this.onBlobReadyCallback({ base64, mimeType });
      }
    };
    reader.onerror = (error) => {
      console.error('[RECORDING] Error reading blob:', error);
      if (this.onErrorCallback) this.onErrorCallback('Error processing recording. Please try again.');
      this.cleanup();
    };
    reader.readAsDataURL(blob);
  }

  isRecording(): boolean {
    return this.mediaRecorder !== null && this.recordingStartTime > 0;
  }

  isPaused(): boolean { return this.paused; }

  /**
   * True once the capture died under us. The session is still open — the consumer can and
   * should still stop it to keep whatever was captured before the loss.
   */
  hasCaptureFailed(): boolean { return this.captureLost; }

  getRecordingTime(): Date {
    if (!this.isRecording()) return new Date(0);
    const elapsed = Date.now() - this.recordingStartTime;
    return new Date(elapsed);
  }

  getElapsedMs(): number {
    if (!this.isRecording()) return 0;
    return Date.now() - this.recordingStartTime;
  }

  cleanup(): void {
    console.log('[RECORDING] Cleanup');

    if (this.blobTimeout) { clearTimeout(this.blobTimeout); this.blobTimeout = null; }
    if (this.sliceInterval) { clearInterval(this.sliceInterval); this.sliceInterval = null; }
    if (this.recordingInterval) { clearInterval(this.recordingInterval); this.recordingInterval = null; }
    if (this.muteTimeout) { clearTimeout(this.muteTimeout); this.muteTimeout = null; }

    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    this.visibilityHandler = null;

    if (this.mediaRecorder) {
      try { this.mediaRecorder.destroy(); } catch { /* ignore */ }
      this.mediaRecorder = null;
    }
    this.releaseStream();

    this.blobProcessed = false;
    this.recordingStartTime = 0;
    this.segmentIndex = 0;
    this.paused = false;
    this.captureLost = false;
    this.interrupted = false;
    this.flushing = false;
    this.lastSegmentAt = 0;

    if (this.stopResolver) { this.stopResolver(); this.stopResolver = null; }

    const stateChangeCallback = this.onStateChangeCallback;

    // This service is a root singleton shared by every recording consumer (AI dialog,
    // WhatsApp voice notes, ...). Reset the configuration so a consumer that tears down
    // does not leave its mode/callbacks behind for the next consumer. Without this the
    // AI dialog's 'segmented' mode would leak into the WhatsApp dialog and its final blob
    // would be routed to the stale onSegment callback instead of onBlobReady.
    this.mode = RecordingService.DEFAULT_MODE;
    this.segmentLengthMs = RecordingService.DEFAULT_SEGMENT_LENGTH_MS;
    this.onBlobReadyCallback = undefined;
    this.onSegmentCallback = undefined;
    this.onErrorCallback = undefined;
    this.onStateChangeCallback = undefined;
    this.onInterruptionCallback = undefined;

    if (stateChangeCallback) {
      stateChangeCallback({
        isRecording: false,
        recordingTime: new Date(0),
        startTime: 0
      });
    }
  }
}
